import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { Loader2 } from "lucide-react";

export default function ProtectedRoute({ children, permission, adminOnly, feature }) {
  const { user, checking } = useAuth();
  const location = useLocation();
  if (checking)
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-jamora" size={32} />
      </div>
    );
  // Only the OAuth consent page is remembered, so that Login brings the person back to it (it cannot be reopened by hand); every other page keeps
  // the former behaviour (Login lands on the application centre).
  if (!user) {
    const remember = location.pathname.startsWith("/oauth/");
    return <Navigate to="/login" replace state={remember ? { from: `${location.pathname}${location.search}` } : undefined} />;
  }
  // A password set by an admin at account creation must be changed before
  // anything else: /profile is the only route left reachable until then.
  if (user.must_change_password && location.pathname !== "/profile") {
    return <Navigate to="/profile" replace />;
  }
  // Role definitions and other explicitly admin-only surfaces stay on role.
  // Sent back with a reason: the dashboard says so (components/AccessNotice.jsx) instead of leaving a silent redirect.
  const denied = (reason) => ({ denied: { path: location.pathname, reason } });
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace state={denied("rights")} />;
  if (permission && !hasPermission(user, permission)) return <Navigate to="/dashboard" replace state={denied("rights")} />;
  // Module switched off on the server (or MongoDB without transactions): the route does not exist for anybody, admin included.
  if (feature && !user.features?.[feature]) return <Navigate to="/dashboard" replace state={denied("feature")} />;
  return children;
}
